import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import { DURATION, EASE } from "@/lib/motionTokens";

type RevealProps = {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  y?: number;
  blur?: number;
  once?: boolean;
};

export function Reveal({
  children,
  className,
  delay = 0,
  y = 14,
  blur = 8,
  once = true,
}: RevealProps) {
  const reduce = useReducedMotion();

  return (
    <motion.div
      className={className}
      initial={reduce ? { opacity: 1 } : { opacity: 0, y, filter: `blur(${blur}px)` }}
      whileInView={reduce ? { opacity: 1 } : { opacity: 1, y: 0, filter: "blur(0px)" }}
      viewport={{ once, amount: 0.35 }}
      transition={{
        delay,
        duration: reduce ? 0 : DURATION.section,
        ease: EASE.out as [number, number, number, number],
      }}
    >
      {children}
    </motion.div>
  );
}

type StaggerProps = {
  children: React.ReactNode;
  className?: string;
  delayChildren?: number;
  stagger?: number;
  once?: boolean;
};

export function Stagger({
  children,
  className,
  delayChildren = 0.06,
  stagger = 0.08,
  once = true,
}: StaggerProps) {
  const reduce = useReducedMotion();

  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once, amount: 0.35 }}
      variants={{
        hidden: {},
        show: {
          transition: reduce ? { staggerChildren: 0 } : { delayChildren, staggerChildren: stagger },
        },
      }}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({
  children,
  className,
  y = 10,
  blur = 8,
}: {
  children: React.ReactNode;
  className?: string;
  y?: number;
  blur?: number;
}) {
  const reduce = useReducedMotion();

  return (
    <motion.div
      className={cn(className)}
      variants={{
        hidden: reduce ? { opacity: 1 } : { opacity: 0, y, filter: `blur(${blur}px)` },
        show: reduce ? { opacity: 1 } : { opacity: 1, y: 0, filter: "blur(0px)" },
      }}
      transition={{ duration: reduce ? 0 : DURATION.base, ease: EASE.out as [number, number, number, number] }}
    >
      {children}
    </motion.div>
  );
}

/** Usare SOLO su micro-copy (2–5 parole), non su paragrafi. */
export function TypeReveal({
  text,
  className,
  delay = 0,
}: {
  text: string;
  className?: string;
  delay?: number;
}) {
  const reduce = useReducedMotion();
  const chars = React.useMemo(() => Array.from(text), [text]);

  if (reduce) return <span className={className}>{text}</span>;

  return (
    <span className={cn("inline-flex", className)}>
      {chars.map((ch, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 6, filter: "blur(6px)" }}
          whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          viewport={{ once: true, amount: 0.8 }}
          transition={{
            delay: delay + i * 0.015,
            duration: DURATION.fast,
            ease: EASE.out as [number, number, number, number],
          }}
        >
          {ch === " " ? "\u00A0" : ch}
        </motion.span>
      ))}
    </span>
  );
}
